/**
 * Daily-games API helpers (generic, keyed by gameKey). Mirrors the project pattern: thin functions that
 * take the `useApiClient()` callback and unwrap the `{ success, data }` envelope.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import type { WordleMark } from '../constants/warmup.constants';
import type { DisplayAvatarId } from '../../avatars/types/avatar.types';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';

type ApiClient = ReturnType<typeof useApiClient>;

export interface WordleGuessRow {
  word: string;
  marks: WordleMark[];
}

export type WordleStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'WON' | 'LOST';

export interface WordleAttemptView {
  status: WordleStatus;
  guesses: WordleGuessRow[];
  guessesUsed: number;
  completedAt?: string | null;
}

export interface WordleStreak {
  current: number;
  max: number;
  lastPlayedAt: string | null;
}

export interface WordleReveal {
  answer: string;
  display: string;
  fullName: string;
  /** Curated answers carry a flag emoji; ELITE answers carry a country name. The reveal renders the
   *  flag from whichever is present (emoji → code, else name → code). One is empty per answer. */
  flag?: string;
  nationality?: string;
  club: string;
}

export interface WordleTodayData {
  gameKey: string;
  puzzleDate: string;
  dayNumber: number;
  puzzle: { length: number; maxGuesses: number };
  attempt: WordleAttemptView;
  streak: WordleStreak;
  reveal?: WordleReveal;
  share?: string;
}

export interface WordleGuessData {
  gameKey: string;
  puzzleDate: string;
  dayNumber: number;
  status: WordleStatus;
  guess: WordleGuessRow;
  attempt: WordleAttemptView;
  streak: WordleStreak;
  reveal?: WordleReveal;
  share?: string;
  coinsAwarded?: number;
  coins?: number;
}

export async function fetchTodayGame(
  apiClient: ApiClient,
  gameKey: string,
): Promise<WordleTodayData> {
  const response = await apiClient<{ success: boolean; data: WordleTodayData; message?: string }>(
    API_ENDPOINTS.games.today(gameKey),
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Failed to load game');
  return payload.data;
}

export async function submitGuess(
  apiClient: ApiClient,
  gameKey: string,
  guess: string,
): Promise<WordleGuessData> {
  const response = await apiClient<{ success: boolean; data: WordleGuessData; message?: string }>(
    API_ENDPOINTS.games.guess(gameKey),
    { method: 'POST', data: { guess } },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Failed to submit guess');
  return payload.data;
}

// ── Leaderboard (score-based games: global, all-time best) ──────────────────────────────────────

export interface GameLeaderboardEntry {
  rank: number;
  userId: string;
  name: string;
  /** Avatar base URL (legacy/custom); used as the FriendAvatar fallback. */
  avatar: string | null;
  avatarId: DisplayAvatarId | null;
  avatarConfig: EquippedMap | null;
  /** The ranked metric value (e.g. HILO best run). */
  score: number;
  isCurrentUser: boolean;
}

/** The viewer's own standing. `rank` is null when unranked (no best > 0, or anonymous). */
export interface GameLeaderboardCurrentUser {
  rank: number | null;
  score: number;
}

export interface GameLeaderboardData {
  entries: GameLeaderboardEntry[];
  currentUser: GameLeaderboardCurrentUser;
  /** Total ranked (real) players with a best > 0 — the denominator for "Top X%". */
  total: number;
}

export async function fetchGameLeaderboard(
  apiClient: ApiClient,
  gameKey: string,
): Promise<GameLeaderboardData> {
  const response = await apiClient<{ success: boolean; data: GameLeaderboardData; message?: string }>(
    API_ENDPOINTS.games.leaderboard(gameKey),
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Failed to load leaderboard');
  return payload.data;
}
